import React, {ReactNode} from 'react';
import PokerPanelButtonFold from 'app/components/poker/poker_controller_panel/PokerPanelButtonFold';
import PokerPanelButtonCheckCall from 'app/components/poker/poker_controller_panel/PokerPanelButtonCheckCall';
import PokerPanelButtonBetRaise from 'app/components/poker/poker_controller_panel/PokerPanelButtonBetRaise';
import PokerPanelButtonPokerGoBet from 'app/components/poker/poker_controller_panel/PokerPanelButtonPokerGoBet';
import PokerBetAmountButtons from 'app/components/poker/poker_controller_panel/PokerBetAmountButtons';
import PokerPanelRadioButton from 'app/components/poker/poker_controller_panel/PokerPanelRadioButton';
import PokerMenuButtonBalance from 'app/components/poker/poker_menu_container/PokerMenuButtonBalance';
import PokerPlayersAmount from 'app/components/poker/poker_players_amount/PokerPlayersAmount';
import PokerMenuButtonDeposit from 'app/components/poker/poker_menu_container/PokerMenuButtonDeposit';
import {inject, observer} from 'mobx-react';
import {PokerStore} from 'app/store/PokerStore';
import {PokerController} from 'app/controllers/PokerController';
import {fixed2} from 'app/utils/fixed';
import I18n from 'app/i18n';
import {isDesktop} from 'app/utils';
import {TableModel} from 'app/store/poker/TableModel';
import {formatCurrency} from 'app/utils/balance/formatCurrency';

interface Props {
    pokerStore?: PokerStore
    controller?: PokerController
    table: TableModel
}

interface State {
    amount: string | number
    minRaiseAmount: number
}

@inject('pokerStore', 'controller')
@observer
export default class PokerControllerPanel extends React.Component<Props, State> {
    table: TableModel;

    constructor(props: Props) {
        super(props);
        this.table = props.table;

        const {table: {currentTable}} = this;

        this.state = {
            amount: currentTable?.action_amounts?.toMinRaiseAmount || '0',
            minRaiseAmount: currentTable?.action_amounts?.toMinRaiseAmount || 0
        };
    }

    componentDidUpdate(_prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        const {table: {currentTable: {action_amounts}}} = this;

        if (action_amounts?.toMinRaiseAmount && prevState.minRaiseAmount !== action_amounts.toMinRaiseAmount) {
            this.setState({amount: action_amounts.toMinRaiseAmount, minRaiseAmount: action_amounts.toMinRaiseAmount});
        }
    }

    resetAmount = (): void => this.changeAmount(this.state.minRaiseAmount);

    check = (): void => {
        this.props.controller.check(this.table.currentTable.table_id);
    };

    call = (): void => {
        this.props.controller.call(this.table.currentTable.table_id);
    };

    fold = (): void => {
        this.props.controller.fold(this.table.currentTable.table_id);
    };

    bet = (): void => {
        this.props.controller.bet(this.table.currentTable.table_id, fixed2(this.state.amount), this.resetAmount);
    };

    allIn = (): void => {
        this.props.controller.allIn(this.table.currentTable.table_id);
    };

    changeAmount = (amount: string | number, isFixed = false): void => {
        const regex = /[^0-9.]/u;

        if (regex.test(amount.toString())) {
            return;
        }

        this.setState({amount: isFixed ? fixed2(amount) : amount});
    };

    isCheck = (): boolean => {
        const maxBet = Math.max(...this.table.currentTable.players?.map(item => item.bet) || [0]);

        return this.table.player.bet >= maxBet;
    };

    isAllIn = (): boolean => {
        const {player, currentTable} = this.table;
        const maxBet = Math.max(...currentTable.players?.map(item => item.bet) || [0]);

        return player.chips < maxBet && player.bet < maxBet || Number(this.state.amount) >= player.chips;
    };

    isPlayerTurn = (): boolean => {
        const {currentTable, player} = this.table;

        return player.position === currentTable.action_index;
    };

    renderRadioButtons(): ReactNode {
        const {isCheck, player, preAction, setPreAction, currentTable: {action_amounts: {callAmount}, id, playmoney}} = this.table;

        if (player.waiting_next_hand || player.sat_out || player.fold) {
            return <></>;
        }

        const canCheck = isCheck();

        return <div className="poker-controller-panel">
            <div className="poker-action-buttons">
                <PokerPanelRadioButton
                    radioId={`${id}fold_action`}
                    radioName={`${id}poker[fold]`}
                    radionTitle={canCheck ? I18n.t('poker.check_fold') : I18n.t('poker.fold')}
                    radioValue={canCheck ? 'check/fold' : 'fold'}
                    additionalName="check_or_fold"
                    selectedValue={preAction}
                    onClick={setPreAction}
                />
                <PokerPanelRadioButton
                    radioId={`${id}check_action`}
                    radioName={`${id}poker[check_fold]`}
                    radionTitle={canCheck ? I18n.t('poker.check') : `${I18n.t('poker.call')} ${formatCurrency(callAmount - player.bet, playmoney)}`}
                    radioValue={canCheck ? 'check' : 'call'}
                    additionalName={`${canCheck ? 'check' : 'call'}${playmoney ? ' pm' : ''}`}
                    selectedValue={preAction}
                    onClick={setPreAction}
                />
            </div>
        </div>;
    }

    render(): ReactNode {
        const {player, currentTable: {pot, big_blind, action_amounts, finished, count_players, table_id, playmoney}, isLoading} = this.table;

        if (!player || finished) {
            return <></>;
        }

        const isPlayerTurn = this.isPlayerTurn();
        const disabled = player.waiting_next_hand || !isPlayerTurn || isLoading;
        const {amount, minRaiseAmount} = this.state;
        const isAllIn = this.isAllIn();
        const isCheck = this.isCheck();

        return <>
            {!player.waiting_next_hand && !player.sat_out && isPlayerTurn
                ? <div className="poker-controller-panel">
                    <div className="poker-action-buttons taplock">
                        <PokerPanelButtonFold disabled={disabled} onClick={this.fold} />
                        <PokerPanelButtonCheckCall
                            disabled={disabled || action_amounts.toCallAmount >= action_amounts.toAllInAmount}
                            onCheck={this.check}
                            onCall={this.call}
                            isCheck={isCheck}
                            amount={action_amounts?.toCallAmount || 0}
                            playmoney={playmoney}
                        />
                        <PokerPanelButtonBetRaise
                            disabled={disabled || !isAllIn && Number(amount) < minRaiseAmount}
                            onClick={this.bet}
                            onAllIn={this.allIn}
                            isAllIn={isAllIn}
                            isRaise={!isCheck}
                            amount={isAllIn ? action_amounts?.toAllInAmount || 0 : amount}
                            playmoney={playmoney}
                        />
                    </div>
                    <div className="poker-action-buttons">
                        {!isDesktop() && <>
                            <div className="poker-item-deposit-mobile">
                                <PokerMenuButtonBalance playmoney={playmoney}/>
                                <PokerMenuButtonDeposit tableId={table_id} playmoney={playmoney}/>
                            </div>
                            <PokerPlayersAmount currentCount={count_players} />
                        </>}
                        <PokerPanelButtonPokerGoBet
                            changeAmount={this.changeAmount}
                            amount={String(amount)}
                            playmoney={playmoney}
                        />
                    </div>
                    <PokerBetAmountButtons
                        currentBetAmount={Number(amount)}
                        changeAmount={this.changeAmount}
                        bigBlind={big_blind}
                        pot={Number(pot) + this.table.playersBetAmount}
                        playerBank={player.chips}
                    />
                </div>
                : this.renderRadioButtons()}
        </>;
    }
}
